import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {
  HistoryOutlined,
  CloseOutlined,
  DeleteOutlined,
  UserOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import {AnimatePresence, motion} from 'framer-motion';
import styles from './dashboardHistory.module.scss';
import {useHistoryStore} from '../../zustand/historyStore';

const listVariants = {
  hidden: {opacity: 0},
  show: {opacity: 1, transition: {staggerChildren: 0.04}},
};
const itemVariants = {
  hidden: {opacity: 0, y: 8},
  show: {opacity: 1, y: 0, transition: {duration: 0.2, ease: 'easeOut'}},
};

const SCOPE_LABELS = {
  user: 'User',
  page: 'Page',
  'wallet-transaction': 'Transaction',
  ticket: 'Ticket',
};
const scopeLabel = scope => SCOPE_LABELS[scope] || scope;
// Scopes whose subtitle names a person and so warrants the person icon.
const PERSON_SCOPES = ['user', 'ticket'];

const formatWhen = ts => {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/**
 * Header "History" button + panel. Super-admin only.
 * Lists saved snapshots (currently user sub-tabs) and restores them on click.
 */
const INITIAL_VISIBLE = 10;

const DashboardHistory = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const adminId = useSelector(state => state?.auth?.userData?.user?.id);
  const roleName = useSelector(
    state => state?.auth?.userData?.user?.activeAdminTab?.name,
  );
  const isSuperAdmin = roleName === 'SUPERADMIN';

  const historyByUser = useHistoryStore(state => state.historyByUser);
  const removeFromHistory = useHistoryStore(state => state.removeFromHistory);
  const clearHistory = useHistoryStore(state => state.clearHistory);
  const entries = historyByUser[adminId] || [];

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    // Collapse back to the first page whenever the panel closes.
    setExpanded(false);
    return undefined;
  }, [open]);

  if (!isSuperAdmin) return null;

  // Newest first (store keeps them that way); show 10, then reveal the rest.
  const visibleEntries = expanded ? entries : entries.slice(0, INITIAL_VISIBLE);
  const hiddenCount = entries.length - visibleEntries.length;

  const restore = entry => {
    if (!entry?.path) return;
    // Pass any saved action / filters so the destination page can re-trigger
    // them (re-open a modal, re-apply filters). Plain entries carry neither.
    const state = {};
    if (entry.action) state.historyAction = entry.action;
    if (entry.filters) state.historyFilters = entry.filters;
    navigate(entry.path, {
      state: Object.keys(state).length ? state : undefined,
    });
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(true)}
        aria-label="Open shortcuts">
        <HistoryOutlined />
        {entries.length > 0 && (
          <span className={styles.badge}>{entries.length}</span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className={styles.overlay}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.2}}
            onMouseDown={() => setOpen(false)}>
            <motion.div
              className={styles.panel}
              initial={{opacity: 0, y: -16, scale: 0.98}}
              animate={{opacity: 1, y: 0, scale: 1}}
              exit={{opacity: 0, y: -16, scale: 0.98}}
              transition={{duration: 0.2, ease: 'easeOut'}}
              onMouseDown={e => e.stopPropagation()}>
              <div className={styles.head}>
                <div className={styles.headTitle}>
                  <HistoryOutlined />
                  <span>Shortcuts</span>
                </div>
                <div className={styles.headActions}>
                  {entries.length > 0 && (
                    <button
                      type="button"
                      className={styles.clearBtn}
                      onClick={() => clearHistory(adminId)}>
                      Clear all
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.closeBtn}
                    onClick={() => setOpen(false)}
                    aria-label="Close shortcuts">
                    <CloseOutlined />
                  </button>
                </div>
              </div>

              <div className={styles.body}>
                {entries.length === 0 ? (
                  <div className={styles.empty}>
                    <HistoryOutlined className={styles.emptyIcon} />
                    <p>No shortcuts yet</p>
                    <span>
                      Use “Add to shortcuts” on a page to pin the exact spot
                      you’re viewing here.
                    </span>
                  </div>
                ) : (
                  <motion.ul
                    className={styles.list}
                    variants={listVariants}
                    initial="hidden"
                    animate="show">
                    {visibleEntries.map(entry => (
                      <motion.li
                        key={entry.id}
                        variants={itemVariants}
                        layout
                        className={styles.item}
                        onClick={() => restore(entry)}>
                        <div className={styles.itemMain}>
                          <span className={styles.itemTitle}>
                            {entry.title}
                          </span>
                          {entry.subtitle && (
                            <span className={styles.itemSub}>
                              {PERSON_SCOPES.includes(entry.context?.scope) && (
                                <UserOutlined />
                              )}
                              <span className={styles.itemSubText}>
                                {entry.subtitle}
                              </span>
                            </span>
                          )}
                          {(entry.meta?.length > 0 ||
                            entry.filters?.summary?.length > 0) && (
                            <span className={styles.itemFilters}>
                              {(entry.meta || []).map(chip => (
                                <span
                                  key={chip.key}
                                  className={styles.itemFilterChip}>
                                  <b>{chip.label}</b>
                                  {chip.value ? `: ${chip.value}` : ''}
                                </span>
                              ))}
                              {(entry.filters?.summary || []).map(chip => (
                                <span
                                  key={chip.key}
                                  className={styles.itemFilterChip}>
                                  <b>{chip.label}</b>
                                  {chip.value ? `: ${chip.value}` : ''}
                                </span>
                              ))}
                            </span>
                          )}
                        </div>
                        <div className={styles.itemMeta}>
                          {entry.hasFilters && (
                            <span className={styles.filterTag}>
                              <FilterOutlined />
                              {entry.filterCount
                                ? `${entry.filterCount} Filter${
                                    entry.filterCount > 1 ? 's' : ''
                                  }`
                                : 'Filters'}
                            </span>
                          )}
                          {entry.context?.scope && (
                            <span className={styles.scopeTag}>
                              {scopeLabel(entry.context.scope)}
                            </span>
                          )}
                          <span className={styles.when}>
                            {formatWhen(entry.createdAt)}
                          </span>
                          <button
                            type="button"
                            className={styles.removeBtn}
                            aria-label="Remove from shortcuts"
                            onClick={e => {
                              e.stopPropagation();
                              removeFromHistory(adminId, entry.id);
                            }}>
                            <DeleteOutlined />
                          </button>
                        </div>
                      </motion.li>
                    ))}
                  </motion.ul>
                )}

                {entries.length > INITIAL_VISIBLE && (
                  <button
                    type="button"
                    className={styles.viewMore}
                    onClick={() => setExpanded(prev => !prev)}>
                    {expanded ? 'Show less' : `View more (${hiddenCount})`}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DashboardHistory;
